import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { UnauthorizedPage } from './UnauthorizedPage';

interface AuthGuardProps {
    appName: string;
    appLogo: string;
    rolePrefix: string;
    auth: {
        isAuthenticated: boolean;
        isLoading: boolean;
        rolesFromToken: string[] | null;
    };
    children: ReactNode;
    loadingComponent?: ReactNode;
    defaultRedirect?: string;
    darwinLoginUrl?: string;
}

/**
 * Generic AuthGuard to handle initialization, authentication, and authorization.
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({
    appName,
    appLogo,
    rolePrefix,
    auth,
    children,
    loadingComponent,
    defaultRedirect = '/',
    darwinLoginUrl,
}) => {
    const location = useLocation();
    const { isAuthenticated, isLoading, rolesFromToken } = auth;


    // 1. Loading state
    if (isLoading) {
        return loadingComponent || (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    const currentPath = location.pathname;
    const isAuthPage = currentPath === '/login' || currentPath === '/callback';

    // 2. Authentication check
    if (!isAuthenticated) {
        // If we're on a public auth page, let it render
        if (isAuthPage) {
            return <>{children}</>;
        }

        // Otherwise, redirect to login
        const redirectTo = currentPath !== '/' ? currentPath : defaultRedirect;

        // Support for apps that use localStorage for redirect (like bid-checker)
        if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.setItem('loginRedirectPath', redirectTo);
        }

        const loginUrl = `/login?return_to=${encodeURIComponent(redirectTo)}`;
        return <Navigate to={loginUrl} replace />;
    }

    // 3. Authorization check
    const hasRequiredRole = (roles: string[] | null): boolean => {
        console.log('🔍 AuthGuard - Checking roles:', {
            appName,
            rolePrefix,
            rolesFromToken: roles,
            hasRoles: !!roles && roles.length > 0
        });

        if (!roles || roles.length === 0) return false;

        const hasRole = roles.some(role =>
            role.toLowerCase().startsWith(rolePrefix.toLowerCase()) ||
            role.toLowerCase().startsWith('darwin') ||
            role.toLowerCase().startsWith('marketplace')
        );

        console.log('🔍 AuthGuard - Role check result:', {
            hasRole,
            rolesChecked: roles.map(r => ({
                role: r,
                matchesPrefix: r.toLowerCase().startsWith(rolePrefix.toLowerCase()),
                matchesDarwin: r.toLowerCase().startsWith('darwin'),
                matchesMarketplace: r.toLowerCase().startsWith('marketplace')
            }))
        });

        return hasRole;
    };

    if (!hasRequiredRole(rolesFromToken)) {
        console.log('❌ AuthGuard - No required role found, showing unauthorized page');
        // Only show unauthorized if we're NOT on an auth page (to avoid weird loops)
        if (!isAuthPage) {
            return <UnauthorizedPage appName={appName} appLogo={appLogo} darwinLoginUrl={darwinLoginUrl} />;
        }
    } else {
        console.log('✅ AuthGuard - User has required role, allowing access');
    }

    // 4. Authorized access
    return <>{children}</>;
};
